import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Why this exists: `position: fixed` is NOT relative to the window when any ancestor has a transform,
// filter, will-change, contain or an overflow/z-index stacking context — which the app shell
// (sidebar z-50, sticky header, TabbedWorkspace panels) does. Anything rendered inside a tab can
// therefore never truly cover the screen. These helpers render into <body> instead.

/** Locks page scroll while mounted (the app behind the overlay must not scroll). */
function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
}

/** Always renders its children directly under <body>, above the app shell. */
export function BodyPortal({ children }) {
  useBodyScrollLock(true);
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

/**
 * Renders children inline (inside its tab) normally, and lifts the SAME DOM out to <body> as a
 * full-window overlay when `fullscreen` is true. The DOM node is moved, not re-created, so the
 * Leaflet map, loaded PDF and all component state survive the switch.
 */
export function MovableHost({
  fullscreen,
  className = "",
  overlayClassName = "fixed inset-0 z-[1300] flex flex-col bg-gray-950",
  children,
}) {
  const placeholderRef = useRef(null);
  const [host] = useState(() =>
    typeof document !== "undefined" ? document.createElement("div") : null,
  );
  useBodyScrollLock(fullscreen);

  useLayoutEffect(() => {
    if (!host || !placeholderRef.current) return;
    const target = fullscreen ? document.body : placeholderRef.current;
    if (host.parentNode !== target) target.appendChild(host);
    host.className = fullscreen
      ? overlayClassName
      : "flex h-full w-full flex-col";
    host.setAttribute("data-takeoff-shell", fullscreen ? "" : null);
    if (!fullscreen) host.removeAttribute("data-takeoff-shell");
  }, [fullscreen, host, overlayClassName]);

  useEffect(() => () => host?.remove(), [host]);

  return (
    <div ref={placeholderRef} className={`h-full w-full ${className}`}>
      {host && createPortal(children, host)}
    </div>
  );
}
